import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";

export default function Register() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [sp] = useSearchParams();
  useEffect(() => { if (sp.get("promo")) sessionStorage.setItem("promo", sp.get("promo").toUpperCase()); }, []); // eslint-disable-line
  const [f, setF] = useState({ name: "", business_name: "", email: "", phone: "", password: "", confirm: "", referral_code: (sp.get("ref") || "").toUpperCase() });
  const [refInfo, setRefInfo] = useState(null);
  useEffect(() => {
    const code = f.referral_code.trim();
    if (code.length < 6) { setRefInfo(null); return; }
    const t = setTimeout(() => axios.get(`${API_BASE}/license/referral/check/${encodeURIComponent(code)}`).then(r => setRefInfo(r.data)).catch(() => setRefInfo(null)), 300);
    return () => clearTimeout(t);
  }, [f.referral_code]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.name.trim() || !f.email.trim() || !f.password) { setErr("Fill name, email and password"); return; }
    if (f.password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (f.password !== f.confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/client/register`, {
        name: f.name.trim(),
        business_name: f.business_name.trim() || undefined,
        email: f.email.trim().toLowerCase(),
        phone: f.phone.trim() || undefined,
        password: f.password,
        referral_code: f.referral_code.trim() || undefined,
      }, { withCredentials: true });
      if (setSession) setSession(data.user, data.token);
      toast.success("Account created", { description: "Redirecting to your portal…" });
      setTimeout(() => nav("/my-portal"), 400);
    } catch (ex) {
      const msg = ex.response?.data?.detail || ex.message || "Registration failed";
      setErr(typeof msg === "string" ? msg : "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell testId="register-page" kicker="Client sign up" title="Create your account" subtitle="Your invoices, documents and task progress in one private portal." asideKicker="Client Handbook" asideTitle="Your invoices, documents, all in one place.">
        <form onSubmit={submit} className="w-full space-y-5" autoComplete="off">

          <div className="space-y-3">
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Your name</Label>
              <Input data-testid="reg-name" value={f.name} onChange={set("name")} required autoComplete="off" placeholder="Full name" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Business / firm name <span className="text-zinc-400 font-normal">(optional)</span></Label>
              <Input data-testid="reg-business" value={f.business_name} onChange={set("business_name")} autoComplete="off" placeholder="Leave blank for personal filings" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Email</Label>
              <Input data-testid="reg-email" type="email" value={f.email} onChange={set("email")} required autoComplete="off" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Phone <span className="text-zinc-400 font-normal">(optional)</span></Label>
              <Input data-testid="reg-phone" value={f.phone} onChange={set("phone")} autoComplete="off" placeholder="+91 98200 12345" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Password</Label>
              <Input data-testid="reg-password" type="password" value={f.password} onChange={set("password")} required autoComplete="new-password" placeholder="Min 6 characters" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Confirm password</Label>
              <Input data-testid="reg-confirm" type="password" value={f.confirm} onChange={set("confirm")} required autoComplete="new-password" placeholder="Re-enter password" />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Referral code <span className="text-zinc-400 font-normal">(optional)</span></Label>
              <Input data-testid="reg-referral" value={f.referral_code} onChange={e => setF({ ...f, referral_code: e.target.value.toUpperCase() })} autoComplete="off" placeholder="MM369-XXXXXXX" />
              {refInfo ? <div data-testid="reg-referral-info" className={`text-[11px] mt-1 ${refInfo.valid ? "text-emerald-700" : "text-rose-600"}`}>{refInfo.valid ? `Referred by ${refInfo.referrer} — you get ${refInfo.referee_days} free days after your first payment` : "Code not recognised"}</div> : null}
            </div>
            {err ? <div data-testid="reg-error" className="text-xs text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2">{err}</div> : null}
            <Button data-testid="reg-submit" type="submit" disabled={busy} className="w-full h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-semibold tracking-wide">
              {busy ? "Creating account…" : "Create account →"}
            </Button>
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 text-center">
              Already registered? <Link to="/login" className="text-zinc-900 underline">Sign in</Link>
            </div>
          </div>
        </form>
    </AuthShell>
  );
}
